import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // API URL: NUR Produktion mit HTTPS
  // API läuft auf der Hauptdomain unter /api
  const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

  useEffect(() => {
    // Check if user is logged in on mount – Token muss validiert werden
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Token validieren bevor wir als eingeloggt gelten
      fetchUserData(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullUserData = response.data;
      localStorage.setItem('user', JSON.stringify(fullUserData));
      setUser(fullUserData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Token ungültig oder abgelaufen → ausloggen
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });
  const { access_token, user_type, user_id, name, is_admin, is_manager, is_ticket_controller } = response.data;
  const userData = { user_type, user_id, name, email, is_admin, is_manager, is_ticket_controller };
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Lade vollständige User-Daten vom Server
      const fullUserResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      const fullUserData = fullUserResponse.data;
      
      localStorage.setItem('user', JSON.stringify(fullUserData));
      setUser(fullUserData);
      return { success: true, userData: fullUserData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Anmeldung fehlgeschlagen',
      };
    }
  };

  const registerUser = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register/user`, userData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Lade vollständige User-Daten vom Server
      const fullUserResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      const fullUserData = fullUserResponse.data;
      
      localStorage.setItem('user', JSON.stringify(fullUserData));
      setUser(fullUserData);
      return { success: true, user: fullUserData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registrierung fehlgeschlagen',
      };
    }
  };

  const registerCompany = async (companyData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register/company`, companyData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Lade vollständige User-Daten vom Server
      const fullUserResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      const fullUserData = fullUserResponse.data;
      
      localStorage.setItem('user', JSON.stringify(fullUserData));
      setUser(fullUserData);
      return { success: true, user: fullUserData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registrierung fehlgeschlagen',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        registerUser,
        registerCompany,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
